/**
 * Admin visual tokens: the system-owned palette for AuthoringShell and the
 * management pages (lists, settings, delivery). Deliberately separate from the
 * shared course tokens (`useTheme()` / `designTokens`): course content, the
 * WYSIWYG editor, previews and exports keep using those, and account themes
 * never change these values.
 *
 * Contrast (sRGB, measured 2026-10-07): text.primary on panel 15.0:1, on
 * canvas 13.8:1; text.secondary on panel 6.2:1, on canvas 5.7:1, on
 * navigation.selected 5.3:1; white on action.primary 10.0:1;
 * border.control on panel 3.8:1, on canvas 3.5:1; focus.ring on panel 5.2:1,
 * on canvas 4.8:1. White on brand.marker is only 4.0:1, so the marker is
 * decoration only: never a text color or a filled button with white text.
 */
export const adminTokens = {
  surface: {
    /** Management-page backdrop, outside content and editor surfaces. */
    canvas: '#F5F5F8',
    /** Lists, forms, dialogs and working cards. */
    panel: '#FFFFFF',
    /** Selected navigation, icon wells, small context summaries. */
    subtle: '#ECECF3',
    /** Occasional branded icon well. */
    rose: '#FFF0F7',
    /** Optional page-introduction strip on management pages only. */
    headerWash: 'linear-gradient(100deg, #FEF8FA 0%, #FBF7FB 35%, #F7F6FC 65%, #F3F4FC 100%)',
    /** Dims the page behind admin dialogs and side panels. */
    scrim: 'rgba(34, 36, 66, 0.45)',
  },
  navigation: {
    selected: '#ECECF3',
  },
  border: {
    /** Decorative panel edges and dividers; never the only boundary of a control. */
    subtle: '#E4E4EC',
    /** Field, select and secondary-button outlines (3:1 or better on every admin surface). */
    control: '#D8D9E5',
  },
  text: {
    primary: '#222442',
    secondary: '#5E5F79',
    /** Disabled labels only (exempt from contrast minimums). */
    disabled: '#9A9BB0',
    link: '#2D3FA6',
    onPrimary: '#FFFFFF',
  },
  action: {
    primary: '#40405D',
    primaryHover: '#31314A',
  },
  brand: {
    /** Narrow active-navigation marker or small decorative accent. */
    marker: '#F7007B',
    roseForeground: '#D4006A',
  },
  focus: {
    ring: '#2563EB',
  },
  /** Client-context summary (the "Client View" banner). */
  context: {
    background: '#FFF4EB',
    border: '#F3D3B8',
    text: '#853A00',
  },
  status: {
    info: { background: '#EFF8FF', border: '#B2DDFF', text: '#1849A9' },
    success: { background: '#ECFDF3', border: '#ABEFC6', text: '#067647' },
    warning: { background: '#FFFAEB', border: '#FEDF89', text: '#93370D' },
    error: { background: '#FEF3F2', border: '#FECDCA', text: '#B42318' },
  },
  typography: {
    /** The shipped Source Sans stack (same family the course defaults use). */
    fontFamily: '"Source Sans 3", "Source Sans Pro", system-ui, sans-serif',
    pageTitle: { size: '28px', lineHeight: 1.2, weight: 700 },
    sectionTitle: { size: '18px', lineHeight: 1.3, weight: 700 },
    body: { size: '14px', lineHeight: 1.5 },
    label: { size: '13px', lineHeight: 1.4 },
    caption: { size: '12px', lineHeight: 1.4 },
  },
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '12px',
    lg: '16px',
    xl: '24px',
    '2xl': '32px',
  },
  radius: {
    control: '8px',
    nav: '10px',
    well: '9px',
    tile: '12px',
    panel: '16px',
    pill: '999px',
  },
  shadow: {
    panel: '0 1px 2px rgba(22, 22, 41, 0.04), 0 2px 6px -2px rgba(22, 22, 41, 0.05)',
    overlay: '0 12px 32px -8px rgba(22, 22, 41, 0.18), 0 2px 6px rgba(22, 22, 41, 0.06)',
  },
  layout: {
    navWidth: '220px',
    pageGutter: '32px',
    pageGutterNarrow: '16px',
  },
} as const;

export type AdminTokens = typeof adminTokens;
export type AdminStatusTone = keyof AdminTokens['status'];
